import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { SVLoader, SVButton } from '../../core';
import { useDeactivateCustomer } from '@/hooks/requests/useCustomers';

type CustomerDeactivateModalProps = {
  id: string;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CustomerDeactivateModal({
  id,
  open,
  setOpen,
}: CustomerDeactivateModalProps) {
  const {
    canDeactivateCustomer,
    isCanDeactivateCustomerPending,
    deactivateCustomer,
    isDeactivateCustomerPending,
  } = useDeactivateCustomer(id, open);

  if (isCanDeactivateCustomerPending) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <SVLoader />
        </DialogContent>
      </Dialog>
    );
  }

  if (!canDeactivateCustomer) {
    return (
      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <Typography variant="h4">Cannot Deactivate Customer</Typography>
          <Typography>
            This customer cannot be deactivated as they are tied to at least one
            active company as the relationship manager.
          </Typography>
        </DialogContent>
        <DialogActions>
          <SVButton variant="outlined" onClick={() => setOpen(false)}>
            Back
          </SVButton>
        </DialogActions>
      </Dialog>
    );
  }

  async function handleDeactivate() {
    try {
      await deactivateCustomer();
      showToast({
        type: 'success',
        data: { content: 'Customer member has been successfully deactivated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h4">Deactivate Customer?</Typography>
        <Typography>
          Are you sure you would like to deactivate this customer?
        </Typography>
      </DialogContent>
      <DialogActions>
        <SVButton variant="outlined" onClick={() => setOpen(false)}>
          No, Don't Deactivate
        </SVButton>
        <SVButton
          variant="error"
          isLoading={isDeactivateCustomerPending}
          onClick={handleDeactivate}>
          Yes, Deactivate
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
